import { IS_PROD } from '@muzly/shared';

import { isUnderRoute, toAppPath } from '../../../core';

/**
 * Universal Link / App Link 로 이 앱에 묶인 호스트. 플레이버마다 하나씩 등록되지만
 * (Android `appLinkHost`, iOS `MUZLY_APPLINKS_HOST`) 웹 빌드는 하나라 둘 다 받는다.
 */
const APP_LINK_HOSTS: readonly string[] = ['app.muzly.io', 'app-dev.muzly.io'];

// `muzly://`(prod)·`muzly-dev://`(dev) — 플레이버별 `urlScheme`. 뒤의 슬래시 개수는 가리지 않는다.
const CUSTOM_SCHEME = /^(?:muzly|muzly-dev):\/*/i;

/**
 * 딥링크 URL → 앱 라우트(`pathname + search + hash`). 받을 수 없는 링크는 `null`.
 *
 * 커스텀 스킴은 `new URL()` 로 host 를 풀지 않고 스킴만 떼어 경로로 붙인다. 비특수 스킴의
 * host 파싱은 엔진마다 다르고(`muzly://result/1` 의 `result` 가 host 인지 path 인지), 구형
 * WebView 가 표준과 다르게 풀어도 같은 경로가 나와야 해서다. 두 갈래 모두 마지막엔
 * `toAppPath` 가 앱 밖으로 풀리는 경로(`/\host` 등)를 걸러낸다.
 */
export const toRoutePath = (rawUrl: string): string | null => {
  const url = rawUrl.trim();
  const scheme = CUSTOM_SCHEME.exec(url);
  if (scheme) {
    return toAppPath(`/${url.slice(scheme[0].length)}`);
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (
    parsed.protocol !== 'https:' ||
    !APP_LINK_HOSTS.includes(parsed.hostname)
  ) {
    return null;
  }
  return toAppPath(parsed.pathname + parsed.search + parsed.hash);
};

/** 공유 링크 게이트웨이 경로. 링크는 `/link?to=<앱 안의 경로>` 모양이다. */
export const LINK_ROUTE = '/link';

/**
 * `/link?to=` 의 목적지. 앱 안의 절대 경로만 받는다 — 이 값은 navigate 뿐 아니라 앱 스킴과
 * 브라우저 폴백 URL 로도 흘러가므로, 다른 origin 으로 풀리는 입력은 여기서 끊는다.
 * 게이트웨이 자신도 거부한다 — 같은 라우트 안의 이동이라 페이지가 다시 판정하지 않고 멈춘다.
 */
export const parseLinkTarget = (raw: string | null): string | null => {
  const path = raw?.startsWith('/') ? toAppPath(raw) : null;
  return path && !isUnderRoute(path, LINK_ROUTE) ? path : null;
};

export interface AppIdentity {
  /** 커스텀 스킴 — 플레이버별 `urlScheme`(`android/app/build.gradle`, iOS `MUZLY_URL_SCHEME`). */
  scheme: 'muzly' | 'muzly-dev';
  /** 플레이버별 `applicationId`. */
  androidPackage: 'io.lemoncloud.muzly' | 'io.lemoncloud.muzly.dev';
}

/** 이 웹 빌드가 열어야 할 앱. 운영 빌드는 운영 앱, 그 외(dev·local)는 개발 앱이다. */
export const appIdentity = (isProd: boolean = IS_PROD): AppIdentity =>
  isProd
    ? { scheme: 'muzly', androidPackage: 'io.lemoncloud.muzly' }
    : { scheme: 'muzly-dev', androidPackage: 'io.lemoncloud.muzly.dev' };
